// Session persistence. Never localStorage (SECURITY.md §2): the session is encrypted with AES-GCM
// and kept in IndexedDB, with a non-extractable key that lives in IndexedDB too.

const DB_NAME = 'finance-dashboard-secure';
const STORE = 'kv';

function idb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const database = await idb();
  return new Promise<T>((resolve, reject) => {
    const tx = database.transaction(STORE, mode);
    const request = fn(tx.objectStore(STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function key(): Promise<CryptoKey> {
  const existing = await withStore<CryptoKey | undefined>('readonly', (s) => s.get('key') as IDBRequest<CryptoKey | undefined>);
  if (existing) return existing;
  const created = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  await withStore('readwrite', (s) => s.put(created, 'key'));
  return created;
}

/**
 * WebCrypto and IndexedDB keys need a secure context (https or localhost). Opened over plain
 * http from another device on the LAN (a phone during development) there is no safe place to
 * persist a session. Development builds then use localStorage, or every reload the phone does
 * on its own would sign the owner out (SECURITY.md §2, development exception); a production
 * build keeps it in this tab's memory only — and production is always https.
 */
const memory = new Map<string, string>();
const canPersist = () => typeof crypto !== 'undefined' && crypto.subtle !== undefined && window.isSecureContext && typeof indexedDB !== 'undefined';
const devKey = (name: string) => `fd.devSession.${name}`;

function saveInsecure(name: string, value: string | null) {
  if (value === null) memory.delete(name);
  else memory.set(name, value);
  if (!import.meta.env.DEV) return;
  try {
    if (value === null) localStorage.removeItem(devKey(name));
    else localStorage.setItem(devKey(name), value);
  } catch {
    // storage blocked: memory only
  }
}

function loadInsecure(name: string): string | null {
  if (memory.has(name)) return memory.get(name)!;
  if (!import.meta.env.DEV) return null;
  try {
    return localStorage.getItem(devKey(name));
  } catch {
    return null;
  }
}

export async function saveSecret(name: string, value: string | null): Promise<void> {
  if (!canPersist()) {
    saveInsecure(name, value);
    return;
  }
  if (value === null) {
    await withStore('readwrite', (s) => s.delete(name));
    return;
  }
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, await key(), new TextEncoder().encode(value));
  await withStore('readwrite', (s) => s.put({ iv, data }, name));
}

export async function loadSecret(name: string): Promise<string | null> {
  if (!canPersist()) return loadInsecure(name);
  try {
    const record = await withStore<{ iv: Uint8Array; data: ArrayBuffer } | undefined>(
      'readonly',
      (s) => s.get(name) as IDBRequest<{ iv: Uint8Array; data: ArrayBuffer } | undefined>,
    );
    if (!record) return null;
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: record.iv as BufferSource }, await key(), record.data);
    return new TextDecoder().decode(plain);
  } catch {
    return null;
  }
}
